/**
 * PendingSignaturesWidget — Dashboard-Kachel „Wartet auf Unterschrift"
 *
 * Listet alle laufenden Zwei-Parteien-Signatur-Workflows (status=in_progress)
 * über alle Mitarbeiter hinweg und zeigt, auf wen gerade gewartet wird.
 * RBAC: nur für Nutzer mit Mitarbeiter-Ansicht (HR/Leitung).
 * Versteckt sich, wenn nichts offen ist.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { PenLine, Clock, ArrowRight, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { signaturesAPI } from '../../lib/api';

const daysSince = (iso) => {
  if (!iso) return null;
  try {
    const then = new Date(iso).getTime();
    return Math.max(0, Math.floor((Date.now() - then) / 86400000));
  } catch { return null; }
};

const isOverdue = (iso) => {
  if (!iso) return false;
  try { return new Date(iso).getTime() < Date.now(); } catch { return false; }
};

export const PendingSignaturesWidget = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [remindingId, setRemindingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    signaturesAPI
      .listWorkflows({ status: 'in_progress' })
      .then((res) => { if (!cancelled) setItems(res.data || []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, []);

  const sendReminder = async (e, wf) => {
    e.stopPropagation();
    setRemindingId(wf.id);
    try {
      const { data } = await signaturesAPI.remindWorkflow(wf.id, { base_url: window.location.origin });
      toast.success(data.message || 'Erinnerung gesendet');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erinnerung fehlgeschlagen');
    } finally {
      setRemindingId(null);
    }
  };

  // Versteckt sich komplett, wenn nichts offen ist (kein Clutter)
  if (items !== null && items.length === 0) return null;
  if (items === null) return null;

  // Überfällige zuerst, danach nach Alter (älteste offene oben)
  const sorted = [...items].sort((a, b) => {
    const oa = isOverdue(a.expires_at) ? 1 : 0;
    const ob = isOverdue(b.expires_at) ? 1 : 0;
    if (oa !== ob) return ob - oa;
    return (daysSince(b.created_at) || 0) - (daysSince(a.created_at) || 0);
  });

  return (
    <Card className="border-border/60" data-testid="pending-signatures-widget">
      <CardHeader className="border-b border-border/40 pb-4">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <PenLine className="h-5 w-5 text-primary" />
          Wartet auf Unterschrift
          <Badge variant="secondary" className="ml-1">{items.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-3">
          {sorted.map((wf) => {
            const current = wf.parties?.[wf.current_index];
            const canRemind = current?.method === 'link' && !!current?.email;
            const days = daysSince(wf.created_at);
            const overdue = isOverdue(wf.expires_at);
            return (
              <div
                key={wf.id}
                className={`flex items-center justify-between gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
                  overdue
                    ? 'bg-red-50 dark:bg-red-950/30 border border-red-300 dark:border-red-900 hover:bg-red-100/70 dark:hover:bg-red-950/50'
                    : 'bg-secondary/30 hover:bg-secondary/50'
                }`}
                onClick={() => wf.employee_id && navigate(`/employees/${wf.employee_id}/edit`)}
                data-testid={`pending-signature-${wf.id}`}
              >
                <div className="min-w-0">
                  <p className="font-medium truncate">{wf.employee_name || wf.title}</p>
                  <p className="text-sm text-muted-foreground truncate">{wf.title}</p>
                  {days !== null && (
                    <p
                      className={`text-xs mt-0.5 ${overdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-muted-foreground'}`}
                      data-testid={`pending-signature-age-${wf.id}`}
                    >
                      seit {days} Tag{days === 1 ? '' : 'en'} offen{overdue ? ' · überfällig' : ''}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge
                    variant="outline"
                    className={`gap-1 ${overdue
                      ? 'border-red-400 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/30'
                      : 'border-amber-400 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30'}`}
                  >
                    <Clock className="h-3 w-3" />
                    {current ? `Warte auf ${current.role_label}` : 'Signatur läuft'}
                  </Badge>
                  {canRemind && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 gap-1"
                      disabled={remindingId === wf.id}
                      onClick={(e) => sendReminder(e, wf)}
                      data-testid={`pending-signature-remind-${wf.id}`}
                    >
                      <Mail className="h-3.5 w-3.5" />
                      Erinnern
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <button
          type="button"
          className="mt-3 w-full inline-flex items-center justify-center gap-1 text-sm text-primary hover:underline"
          onClick={() => navigate('/employees')}
          data-testid="pending-signatures-all-btn"
        >
          Zu den Mitarbeitern <ArrowRight className="h-4 w-4" />
        </button>
      </CardContent>
    </Card>
  );
};

export default PendingSignaturesWidget;
